import { MerchantNav } from "../components/MerchantNav";

interface Section {
  id: string;
  title: string;
  body: JSX.Element;
}

const SECTIONS: Section[] = [
  {
    id: "getting-started",
    title: "Getting started",
    body: (
      <>
        <p>
          Welcome to Orbit. This page walks you through every menu in
          your merchant dashboard. If you're brand new, follow the
          steps in order — each section builds on the last.
        </p>
        <ol className="list-decimal pl-5 space-y-1 mt-2">
          <li>Set your scan rules in <b>Settings</b>.</li>
          <li>Create one or two rewards in <b>Rewards</b>.</li>
          <li>Train your cashier on <b>Redeem</b> and <b>Transactions</b>.</li>
          <li>Invite your team in <b>Team</b> (Owner only).</li>
        </ol>
      </>
    ),
  },
  {
    id: "dashboard",
    title: "Dashboard",
    body: (
      <p>
        At-a-glance numbers for today and the last 7 days: visits,
        sales, points awarded, and recent activity. Use this as your
        morning check-in. Owner and Manager only.
      </p>
    ),
  },
  {
    id: "transactions",
    title: "Transactions",
    body: (
      <>
        <p>
          Every scan and redemption your shop has made shows up here,
          newest first. Click any row to see details, the customer,
          and points awarded.
        </p>
        <p className="mt-2">
          <b>Owner / Manager only:</b> use the <i>Void</i> button to
          cancel a wrong scan, or <i>Refund</i> to give back a partial
          amount. Both actions reverse the points automatically.
        </p>
      </>
    ),
  },
  {
    id: "customers",
    title: "Customers",
    body: (
      <>
        <p>
          Search any customer by phone number or name to see their
          balance, visit history, and rewards earned at your shop.
        </p>
        <p className="mt-2">
          <b>Owner / Manager only:</b> use <i>Adjust balance</i> to
          add or remove points by hand (every adjustment is logged
          with a reason for your audit trail).
        </p>
      </>
    ),
  },
  {
    id: "redeem",
    title: "Redeem",
    body: (
      <>
        <p>
          The cashier-facing screen. To apply a reward:
        </p>
        <ol className="list-decimal pl-5 space-y-1 mt-2">
          <li>Ask the customer to open their voucher and show the QR.</li>
          <li>Scan it with this page open.</li>
          <li>Confirm the reward and tap <i>Redeem</i>.</li>
        </ol>
        <p className="mt-2">
          The voucher burns immediately and cannot be used again. If
          you tap by mistake, use <i>Cancel</i> within 60 seconds.
        </p>
      </>
    ),
  },
  {
    id: "rewards",
    title: "Rewards",
    body: (
      <>
        <p>
          Define what customers can spend their points on — for
          example "Free coffee for 100 points" or "10% off for 200".
        </p>
        <p className="mt-2">
          You can pause a reward without deleting it. Customers who
          already redeemed it keep their voucher; new redemptions
          stop right away.
        </p>
      </>
    ),
  },
  {
    id: "marketplace",
    title: "Marketplace",
    body: (
      <>
        <p>
          Convert orders from Shopee, Lazada, and TikTok Shop into
          Orbit claim codes. Export your orders to a spreadsheet,
          upload it here, then print the claim slips and tuck one
          inside each parcel.
        </p>
        <p className="mt-2">
          When the buyer scans the slip they're enrolled with you and
          earn points just like a walk-in customer. Codes can be
          voided from the <i>Codes</i> tab if a parcel is returned.
        </p>
      </>
    ),
  },
  {
    id: "surprises",
    title: "Surprises",
    body: (
      <p>
        Drop a small extra reward into a random visit — a free cookie,
        a sticker, a discount on the next visit. Add items to the
        pool, set how often a customer hits one, and Orbit handles
        the lottery. Great for word-of-mouth.
      </p>
    ),
  },
  {
    id: "streaks",
    title: "Streaks",
    body: (
      <p>
        Reward customers who visit a few days or weeks in a row. Set
        the milestones (e.g. 3-day streak = bonus 50 points) and the
        engine fires them automatically when the customer scans.
      </p>
    ),
  },
  {
    id: "inbox",
    title: "Inbox (broadcasts)",
    body: (
      <>
        <p>
          Send a message to your enrolled customers. Pick the channels
          (in-app inbox, email, SMS) and the audience, write your
          message, and send.
        </p>
        <ul className="list-disc pl-5 space-y-1 mt-2">
          <li><b>Inbox</b> is free.</li>
          <li><b>Email</b> is free but only reaches customers who gave you their email.</li>
          <li><b>SMS</b> uses your SMS credits — see the badge at the top of the composer for your remaining balance.</li>
        </ul>
        <p className="mt-2">
          Marketing-category messages skip customers who opted out of
          marketing. Service announcements (transactional) ignore the
          opt-out — use that category sparingly.
        </p>
      </>
    ),
  },
  {
    id: "plans",
    title: "Plans (subscription)",
    body: (
      <p>
        Your current Orbit subscription tier and a request form to
        upgrade or downgrade. <b>Owner only.</b> Upgrade requests are
        approved by the Orbit team within one business day.
      </p>
    ),
  },
  {
    id: "team",
    title: "Team (users)",
    body: (
      <>
        <p>
          <b>Owner only.</b> Invite your manager and cashier staff so
          each person has their own login (no more sharing the
          token).
        </p>
        <p className="mt-2 font-semibold">Roles:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>
            <b>Owner</b> — full access including team management,
            plans, and refunds.
          </li>
          <li>
            <b>Manager</b> — everything except adding people and
            changing plans.
          </li>
          <li>
            <b>Staff</b> — cashier-only: scan, redeem, look up
            customers, see today's transactions. Cannot void, refund,
            adjust balances, send broadcasts, or change settings.
          </li>
        </ul>
        <p className="mt-2">
          Newly invited people get a temporary password and must
          choose their own on first login.
        </p>
      </>
    ),
  },
  {
    id: "settings",
    title: "Settings",
    body: (
      <p>
        Your shop name, scan mode (per-transaction or per-visit),
        max transaction amount, and the loyalty mode (points or
        stamps). Owner and Manager only.
      </p>
    ),
  },
  {
    id: "tips",
    title: "Daily tips",
    body: (
      <ul className="list-disc pl-5 space-y-1">
        <li>
          Keep the <b>Redeem</b> page open on your cashier device
          during business hours — it's the fastest way to scan.
        </li>
        <li>
          Check <b>Dashboard</b> first thing each morning to spot
          unusual patterns.
        </li>
        <li>
          Send a short <b>Inbox</b> message every 1–2 weeks. Customers
          who hear from you visit twice as often.
        </li>
        <li>
          If a cashier leaves, suspend their account in <b>Team</b>{" "}
          immediately. Their sessions are revoked within 15 minutes.
        </li>
      </ul>
    ),
  },
];

export default function MerchantHelpPage() {
  return (
    <div className="min-h-screen bg-cream px-4 py-6 max-w-4xl mx-auto">
      <MerchantNav current="help" />

      <main className="mt-8 space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-navy">How to use Orbit</h1>
          <p className="text-ink-dark mt-1">
            A page-by-page guide to your merchant dashboard.
          </p>
        </div>

        {/* On-page table of contents */}
        <nav className="rounded-lg border border-amber-light bg-white p-4">
          <h2 className="text-sm font-semibold text-navy uppercase tracking-wide">
            Jump to
          </h2>
          <ul className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
            {SECTIONS.map((s) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  className="text-navy hover:underline"
                  data-testid={`help-toc-${s.id}`}
                >
                  {s.title}
                </a>
              </li>
            ))}
          </ul>
        </nav>

        {SECTIONS.map((s) => (
          <section
            key={s.id}
            id={s.id}
            className="rounded-lg border border-amber-light bg-white p-5 scroll-mt-6"
          >
            <h2 className="text-xl font-semibold text-navy mb-2">
              {s.title}
            </h2>
            <div className="text-ink-dark text-sm leading-relaxed">
              {s.body}
            </div>
          </section>
        ))}

        <p className="text-center text-xs text-ink-dark/70 pt-4">
          Need more help? Email{" "}
          <a href="mailto:support@orbit.m42" className="underline">
            support@orbit.m42
          </a>
          .
        </p>
      </main>
    </div>
  );
}
